// =====================================================================
// claims.js, single source of truth for marketing-headline numbers.
// ---------------------------------------------------------------------
// Anywhere the landing page, deck, one-pagers, or social previews
// quote a top-line claim ("80% bill reduction", "5.7× compression",
// "128K context on a single GPU"), the value should be imported
// from here, not hardcoded.
//
// When the underlying benchmark improves (or regresses), update this
// file and the whole site refreshes consistently. The hero stat
// strip, the §02 metric cards, the SEO meta description, the OG
// image generator, and the Playground default compression ratio all
// read from here.
//
// NB: This file intentionally does NOT pull from pricing.js. The
// commission rates (15% to 20% rack rates)
// have their own source-of-truth in pricing.js because they also
// drive the billing math. The customer-share inverse ("You keep
// 80-85%") is duplicated here because the hero is a marketing
// surface, not a billing surface.
// =====================================================================

// ── Compression / VRAM ────────────────────────────────────────────────
// 5.7× is a representative reduction on the multi-turn agent trajectories LM
// targets: on a realistic long coding-agent history, the cross-turn compressor
// elides stale observations while keeping the live file state and failing tests
// byte-exact. The playground slider defaults here.
export const HEADLINE_COMPRESSION_X = 5.7;

// 80% is the bill-reduction claim at production scale. Derived from
// (compressed_tokens / raw_tokens) ≈ 0.20 at our 5-6× compression
// ratio, dominated by input-token cost on most cloud LLM pricing.
export const HEADLINE_BILL_REDUCTION_PCT = 80;

// 65K is the longest context Mode 2 runs at, and the paper-cited claim is a
// MEMORY result: on an RTX 3090 (24 GB) it shows strict linear memory scaling
// to 65,536 tokens at 19.29 GB, while a standard MHA baseline exhausts memory
// at 32,768. That is the number a reader can verify against the preprint.
// Source: paper (Liquid Memory Preprint), dist_public/LiquidMemory_AOTI_sm_90_L65536_trained.pt2.
export const HEADLINE_MAX_CONTEXT_K = 65;
export const HEADLINE_MAX_CONTEXT_LABEL = "65K";
// Measured LATENCY speedup vs FlashAttention-2, fp32 on H100 (per README):
// 16.9x at 32K, the largest length where FA2 still runs. At 65K FA2 OOMs, so
// there is no FA2 number to divide by and we do NOT claim a multiple there.
// (The old "38x at 65K vs FA2 on H200" appeared on the site but is in neither
// the paper nor the H100 benchmark; removed until a real FA2-at-65K run exists.)
export const HEADLINE_LONG_CTX_SPEEDUP_X = 17;

// 5 minutes is the wall-clock for `python benchmark.py` end-to-end
// on an A100, including model load. Quoted in three places
// (hero CTA tooltip, §02 callout, install copy).
export const HEADLINE_INSTALL_MINUTES = 5;

// ── Pricing surface (customer-facing inverse) ─────────────────────────
// The marginal-bracket commission is 20% / 18% / 15% rack rates.
// Customer keeps 100 - rate, so the customer-share range is 80%
// (worst tier-1 commission) to 85% (best blended tier-3 rate).
export const HEADLINE_CUSTOMER_KEEPS_LOW_PCT = 80;
export const HEADLINE_CUSTOMER_KEEPS_HIGH_PCT = 85;

// ── Provider list ─────────────────────────────────────────────────────
// Order of these strings matters: they drive the provider chip row
// under §04 and the Verify pre-block in /portal/connection. The
// first three are the cloud LLMs most cold-outreach buyers already
// pay; the last two are the open-source / self-host destinations
// (LiteLLM lets us forward to either).
export const SUPPORTED_PROVIDERS = [
  "OpenAI",
  "Anthropic",
  "Gemini",
];

// ── Formatted helpers ─────────────────────────────────────────────────
// Convenience strings the JSX can drop in without doing template
// math at the call site. Update the formats here once.
export const fmtCompression = () => `${HEADLINE_COMPRESSION_X}×`;
export const fmtBillReduction = () => `${HEADLINE_BILL_REDUCTION_PCT}%`;
export const fmtMaxContext = () => `${HEADLINE_MAX_CONTEXT_K}K`;
export const fmtCustomerKeepsRange = () =>
  `${HEADLINE_CUSTOMER_KEEPS_LOW_PCT}-${HEADLINE_CUSTOMER_KEEPS_HIGH_PCT}%`;
